import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { AnimalDetailPanel } from '../../../components/animal-detail-panel';
import { BulkActionBar } from '../../../components/bulk-action-bar';
import { BulkUpdateDialog } from '../../../components/bulk-update-dialog';
import { HerdTable } from '../../../components/herd-table';
import { Skeleton } from '../../../components/ui/skeleton';
import { useDebounce } from '../../../hooks/use-debounce';
import { useMediaQuery } from '../../../hooks/use-media-query';
import { api } from '../../../lib/api';
import { DEFAULT_VISIBLE_KEYS } from '../../../lib/herd-columns';
import { herdBaseKey, herdQuery } from '../../../lib/queries';
import type { HerdAnimal } from '../../../lib/schemas';
import { cn } from '../../../lib/utils';

export const Route = createFileRoute('/_auth/herd/')({
  component: HerdListPage,
});

type StatusFilter = 'All' | 'Milking' | 'Dry' | 'Close-up' | 'Heifer';

const STATUS_FILTERS: StatusFilter[] = ['All', 'Milking', 'Dry', 'Close-up', 'Heifer'];
const PAGE_SIZE = 50;

const STATUS_BADGE_COLORS: Record<string, string> = {
  Milking: 'bg-ndd-600',
  Dry: 'bg-amber-600',
  'Close-up': 'bg-purple-600',
  Heifer: 'bg-blue-600',
  Calf: 'bg-cyan-600',
  Dead: 'bg-gray-600',
  Sold: 'bg-gray-500',
};

function AnimalCard({ animal }: { animal: HerdAnimal }) {
  const dueAlert =
    animal.days_until_calving != null &&
    animal.days_until_calving > 0 &&
    animal.days_until_calving <= 30;

  return (
    <div className="rounded-lg bg-ndd-900 px-4 py-3">
      <div className="flex items-start justify-between">
        <div>
          <span className="text-lg font-bold">{animal.animal_id}</span>
          {animal.name && <span className="ml-2 text-ndd-300">{animal.name}</span>}
        </div>
        <span
          className={cn(
            'rounded-full px-2 py-0.5 text-xs font-semibold text-white',
            STATUS_BADGE_COLORS[animal.status] ?? 'bg-gray-600'
          )}
        >
          {animal.status}
        </span>
      </div>
      <div className="mt-1 flex flex-wrap gap-3 text-sm text-ndd-400">
        {animal.dim != null && <span>DIM: {animal.dim}</span>}
        {animal.barn_group && <span>Group {animal.barn_group}</span>}
        {animal.lactation_number != null && <span>Lac: {animal.lactation_number}</span>}
        {dueAlert && animal.expected_calving_date && (
          <span className="text-orange-400 font-medium">Due {animal.expected_calving_date}</span>
        )}
      </div>
    </div>
  );
}

function HerdListPage() {
  const { session } = Route.useRouteContext();
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const queryClient = useQueryClient();

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('All');
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebounce(searchInput, 300);
  const [offset, setOffset] = useState(0);
  const [allAnimals, setAllAnimals] = useState<HerdAnimal[]>([]);

  // Desktop state
  const [visibleColumns, setVisibleColumns] = useState<Array<keyof HerdAnimal>>(() => {
    try {
      const saved = localStorage.getItem('herd-visible-columns');
      return saved ? (JSON.parse(saved) as Array<keyof HerdAnimal>) : DEFAULT_VISIBLE_KEYS;
    } catch {
      return DEFAULT_VISIBLE_KEYS;
    }
  });
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [selectedIds, setSelectedIds] = useState(new Set<string>());
  const [detailAnimalId, setDetailAnimalId] = useState<string | null>(null);
  const [bulkDialogType, setBulkDialogType] = useState<'group' | 'status' | null>(null);

  // Reset pagination when search or status filter changes
  // biome-ignore lint/correctness/useExhaustiveDependencies: debouncedSearch/statusFilter trigger the reset, setters are stable
  useEffect(() => {
    setOffset(0);
    setAllAnimals([]);
  }, [debouncedSearch, statusFilter]);

  const queryParams = {
    ...(statusFilter !== 'All' && { status: statusFilter }),
    ...(debouncedSearch && { search: debouncedSearch }),
    limit: PAGE_SIZE,
    offset,
    ...(sortBy && { sort_by: sortBy, sort_dir: sortDir }),
  };

  const { data, isLoading } = useQuery(herdQuery(queryParams));

  // Accumulate pages
  useEffect(() => {
    if (data?.animals) {
      if (offset === 0) {
        setAllAnimals(data.animals);
      } else {
        setAllAnimals((prev) => [...prev, ...data.animals]);
      }
    }
  }, [data, offset]);

  const total = data?.total ?? 0;
  const hasMore = allAnimals.length < total;

  function loadMore() {
    setOffset((prev) => prev + PAGE_SIZE);
  }

  function handleSort(column: string) {
    if (sortBy === column) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortDir('asc');
    }
    setOffset(0);
    setAllAnimals([]);
  }

  function handleColumnVisibilityChange(columns: Array<keyof HerdAnimal>) {
    setVisibleColumns(columns);
    try {
      localStorage.setItem('herd-visible-columns', JSON.stringify(columns));
    } catch {
      // ignore storage errors
    }
  }

  const cellEditMutation = useMutation({
    mutationFn: ({ animalId, field, value }: { animalId: string; field: string; value: unknown }) =>
      api.patch(`/api/herd/${animalId}`, { updates: { [field]: value } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: herdBaseKey });
    },
  });

  if (isDesktop) {
    return (
      <div className="space-y-4 max-w-7xl">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">Herd Management</h1>
        </div>

        {/* Filters */}
        <div className="flex gap-3 items-center">
          <div className="flex gap-2">
            {STATUS_FILTERS.map((status) => (
              <button
                key={status}
                type="button"
                onClick={() => setStatusFilter(status)}
                className={cn(
                  'rounded-full px-3 py-1.5 text-sm font-medium',
                  statusFilter === status
                    ? 'bg-ndd-500 text-white'
                    : 'bg-ndd-900 text-ndd-300 hover:bg-ndd-800'
                )}
              >
                {status}
              </button>
            ))}
          </div>
          <input
            type="text"
            placeholder="Search…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="rounded-lg bg-ndd-900 px-3 py-1.5 text-sm text-white placeholder-ndd-500 w-48 focus:outline-none focus:ring-2 focus:ring-ndd-400"
          />
        </div>

        {/* Bulk action bar */}
        {selectedIds.size > 0 && (
          <BulkActionBar
            selectedCount={selectedIds.size}
            onMoveToGroup={() => setBulkDialogType('group')}
            onChangeStatus={() => setBulkDialogType('status')}
            onClearSelection={() => setSelectedIds(new Set())}
          />
        )}

        <HerdTable
          animals={allAnimals}
          total={total}
          isLoading={isLoading && offset === 0}
          role={session.role}
          visibleColumns={visibleColumns}
          onColumnVisibilityChange={handleColumnVisibilityChange}
          sortBy={sortBy}
          sortDir={sortDir}
          onSort={handleSort}
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
          onCellEdit={(animalId, field, value) =>
            cellEditMutation.mutate({ animalId, field, value })
          }
          onAnimalClick={setDetailAnimalId}
          onLoadMore={loadMore}
          hasMore={hasMore}
        />

        <AnimalDetailPanel
          animalId={detailAnimalId}
          onClose={() => setDetailAnimalId(null)}
          role={session.role}
        />

        {bulkDialogType && (
          <BulkUpdateDialog
            open={!!bulkDialogType}
            onOpenChange={(open) => {
              if (!open) setBulkDialogType(null);
            }}
            animalIds={[...selectedIds]}
            updateType={bulkDialogType}
            onSuccess={() => setSelectedIds(new Set())}
          />
        )}
      </div>
    );
  }

  // Mobile layout
  return (
    <div className="mx-auto max-w-lg px-4 py-4 space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <h1 className="text-2xl font-bold">Herd</h1>
        {total > 0 && (
          <span className="rounded-full bg-ndd-800 px-3 py-1 text-sm font-semibold">{total}</span>
        )}
      </div>

      {/* Status filter */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {STATUS_FILTERS.map((status) => (
          <button
            key={status}
            type="button"
            onClick={() => setStatusFilter(status)}
            className={cn(
              'shrink-0 rounded-full px-4 py-2 text-sm font-medium',
              statusFilter === status
                ? 'bg-ndd-500 text-white'
                : 'bg-ndd-900 text-ndd-300 hover:bg-ndd-800'
            )}
          >
            {status}
          </button>
        ))}
      </div>

      {/* Search */}
      <input
        type="text"
        inputMode="search"
        placeholder="Search by tag or name..."
        value={searchInput}
        onChange={(e) => setSearchInput(e.target.value)}
        className="w-full rounded-lg bg-ndd-900 px-4 py-3 text-white placeholder-ndd-500"
      />

      {/* List */}
      {isLoading && offset === 0 ? (
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-20 w-full" />
          ))}
        </div>
      ) : allAnimals.length === 0 ? (
        <p className="py-8 text-center text-ndd-400">No animals found</p>
      ) : (
        <div className="space-y-2">
          {allAnimals.map((animal) => (
            <AnimalCard key={animal.animal_id} animal={animal} />
          ))}
        </div>
      )}

      {/* Load more */}
      {hasMore && !isLoading && (
        <button
          type="button"
          onClick={loadMore}
          className="w-full rounded-lg bg-ndd-800 py-3 text-sm font-medium hover:bg-ndd-700"
        >
          Load more ({total - allAnimals.length} remaining)
        </button>
      )}
      {isLoading && offset > 0 && <Skeleton className="h-12 w-full" />}
    </div>
  );
}
